import React, { FunctionComponent, ReactNode, useEffect, useState } from 'react';
import { ColumnProps, PaginationConfig, SortOrder } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';
import Loadable from '@app/utils/Loadable';
import { pluralize } from '@app/utils/stringUtils';
import { TableImportantText, TableStandardText } from '@app/components/tables/Table';
import { CaptionStartTransparentBlack600 } from '@app/components/Text';
import NakedFormInput from '@app/components/inputs/NakedFormInput';
import InputBox from '@app/components/inputs/inputBox/InputBox';
import { Organization } from '@app/domain/userManagement/organizations';
import TableOfCards from '@app/components/tables/TableOfCards';
import { getColumnMultiValueConstSelectServerFilterProps } from '@app/components/tableFilters/multiValue/constsFilterCreator';
import { MultiselectValue } from '@app/components/tableFilters/multiValue/FilterColumnMultiValuePopup';
import OrganizationActionsMenu from '@backee/routes/organizations/allOrganizationsPage/OrganizationActionsMenu';
import { OrganizationsSortCriteria, OrganizationsSortOrder } from '@backee/services/organizationsServices';

export const ORGANIZATIONS_PAGE_SIZE_OPTIONS = ['10', '25', '50', '100'];

export interface OrganizationsTableState {
  page: number;
  pageSize: number;
  sort: OrganizationsSortCriteria;
  order: OrganizationsSortOrder;
  serviceIds: string[];
}

interface OrganizationsTableProps {
  id: string;
  dataTestId?: string;
  organizationsPage: Loadable<PaginatedData<Organization>>;
  searchTerm: string;
  onSearchTermChange: (searchTerm: string) => void;
  tableState: OrganizationsTableState;
  onTableStateChange: (tableState: OrganizationsTableState) => void;
  serviceTypeOptions: Loadable<MultiselectValue[]>;
  canAddUsersToOrganization: boolean;
  onItemClick: (item: Organization) => void;
  onItemManageUsers: (item: Organization) => void;
  onItemAddUsers: (item: Organization) => void;
  className?: string;
}

const OrganizationsTable: FunctionComponent<OrganizationsTableProps> = (props) => {
  const {
    id,
    dataTestId,
    organizationsPage,
    searchTerm,
    onSearchTermChange,
    tableState,
    onTableStateChange,
    serviceTypeOptions,
    canAddUsersToOrganization,
    onItemClick,
    onItemManageUsers,
    onItemAddUsers,
    className,
  } = props;

  const [lastResolvedTotal, setLastResolvedTotal] = useState(0);

  useEffect(() => {
    if (organizationsPage.isResolved()) {
      setLastResolvedTotal(organizationsPage.result.totalElements);
    }
  }, [organizationsPage]);

  function onChange(
    newPagination: PaginationConfig,
    newFilters: Partial<Record<keyof Organization | 'services', string[]>>,
    newSorter: SorterResult<Organization>,
  ): void {
    const pageSize = newPagination.pageSize ?? tableState.pageSize;
    const sort: OrganizationsSortCriteria = newSorter.order && newSorter.columnKey === 'services' ? 'ServiceCount' : 'Name';
    const order: OrganizationsSortOrder = newSorter.order === 'descend' ? 'DESC' : 'ASC';
    const serviceIds = newFilters.services ?? [];

    const isQueryChanged =
      pageSize !== tableState.pageSize ||
      sort !== tableState.sort ||
      order !== tableState.order ||
      serviceIds.length !== tableState.serviceIds.length ||
      serviceIds.some((serviceId) => !tableState.serviceIds.includes(serviceId));

    onTableStateChange({
      page: isQueryChanged ? 1 : (newPagination.current ?? tableState.page),
      pageSize,
      sort,
      order,
      serviceIds,
    });
  }

  const total = organizationsPage.isResolved() ? organizationsPage.result.totalElements : lastResolvedTotal;

  const paginationConfig: PaginationConfig = {
    position: 'both',
    current: tableState.page,
    pageSize: tableState.pageSize,
    // antd drops the whole bar at a total of 0, so it would vanish while a page loads and on an empty result
    total: Math.max(total, 1),
    showTotal: (_total, [start, end]): ReactNode => (
      <CaptionStartTransparentBlack600.span>
        {toPaginationLabel(organizationsPage, tableState, total, start, end)}
      </CaptionStartTransparentBlack600.span>
    ),
    showSizeChanger: true,
    pageSizeOptions: ORGANIZATIONS_PAGE_SIZE_OPTIONS,
    // Else a page size that fits every row hides the size changer too, with no way back to a smaller page
    hideOnSinglePage: false,
  };

  return (
    <div>
      <InputBox appearance='line' colorScheme='secondary'>
        <NakedFormInput
          name='organizations-table-search'
          dataTestId='organizations-table-search'
          type='text'
          colorScheme='secondary'
          placeholderStyle='onlyWhenEmpty'
          heightType='thick'
          clearable
          placeholder='🔍 Search for Id / Name'
          value={searchTerm}
          onChange={(newValue): void => onSearchTermChange(newValue ?? '')}
          autoFocus
        />
      </InputBox>
      <TableOfCards
        id={id}
        dataTestId={dataTestId}
        columns={createColumns(tableState, serviceTypeOptions, canAddUsersToOrganization, onItemManageUsers, onItemAddUsers)}
        dataSource={organizationsPage.result?.content ?? []}
        className={className}
        loading={!organizationsPage.isResolved()}
        pagination={paginationConfig}
        onChange={onChange}
        onRow={(record: Organization): { onClick: React.MouseEventHandler<HTMLDivElement> } => {
          return {
            onClick: (e): void => {
              e.preventDefault();
              e.stopPropagation();
              onItemClick(record);
            }, // click row
          };
        }}
        rowClickable
        rowKey={(organization: Organization): string => organization.id}
      />
    </div>
  );
};

export default OrganizationsTable;

function toPaginationLabel(
  organizationsPage: Loadable<PaginatedData<Organization>>,
  tableState: OrganizationsTableState,
  total: number,
  start: number,
  end: number,
): string {
  if (!organizationsPage.isResolved()) {
    const pageStart = (tableState.page - 1) * tableState.pageSize + 1;
    return `${pageStart}-${pageStart + tableState.pageSize - 1} of ?`;
  }

  return total === 0 ? '0 of 0' : `${start}-${end} of ${total}`;
}

function toAntdSortOrder(tableState: OrganizationsTableState, sort: OrganizationsSortCriteria): SortOrder | boolean {
  if (tableState.sort !== sort) {
    return false;
  }

  return tableState.order === 'DESC' ? 'descend' : 'ascend';
}

const createColumns = (
  tableState: OrganizationsTableState,
  serviceTypeOptions: Loadable<MultiselectValue[]>,
  canAddUsersToOrganization: boolean,
  onItemManageUsers: (item: Organization) => void,
  onItemAddUsers: (item: Organization) => void,
): ColumnProps<Organization>[] => {
  return [
    {
      title: <div data-testid='lblName'>Organization Name</div>,
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: toAntdSortOrder(tableState, 'Name'),
      render: (text: any, record: Organization): React.ReactNode => (
        <TableImportantText data-testid='organizations-table-record-name'>{record.name}</TableImportantText>
      ),
    },
    {
      title: <div data-testid='lblServices'>Services</div>,
      dataIndex: 'services',
      key: 'services',
      width: '180px',
      sorter: true,
      sortOrder: toAntdSortOrder(tableState, 'ServiceCount'),
      render: (text: any, record: Organization): React.ReactNode => (
        <TableStandardText data-testid='organizations-table-record-services'>
          {record.serviceIds.length} {pluralize('service', record.serviceIds.length)}
        </TableStandardText>
      ),
      ...getColumnMultiValueConstSelectServerFilterProps<Organization>(serviceTypeOptions, tableState.serviceIds, true),
    },
    {
      title: '',
      dataIndex: 'actions',
      key: 'actions',
      width: '60px',
      render: (text: any, record: Organization): React.ReactNode => {
        return (
          <OrganizationActionsMenu
            organizationId={record.id}
            canAddUsersToOrganization={canAddUsersToOrganization}
            onAddUsers={(): void => onItemAddUsers(record)}
            onManageUsers={(): void => onItemManageUsers(record)}
          />
        );
      },
    },
  ];
};
